import React from 'react'

import { Box, Typography, Stack, Paper, Button, Alert } from '@mui/material'
import {
  IconAlertCircle,
  IconRefresh,
  IconChartColumn,
} from '@tabler/icons-react'

interface TestResult {
  objectId: string
  value?: any
  reasoning?: string
  error?: string
}

interface TestResultsReviewProps {
  results: TestResult[]
  propertyDefinition: {
    name: string
    description: string
  }
  onRefineDefinition: () => void
  onRunBackfill: () => void
  isProcessing?: boolean
}

const TestResultsReview: React.FC<TestResultsReviewProps> = ({
  results,
  propertyDefinition,
  onRefineDefinition,
  onRunBackfill,
  isProcessing = false,
}) => {
  const hasTestedRecords = results.length > 0

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="h4"
              gutterBottom
            >
              Test Results
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Review how your property performed on test records
            </Typography>
          </Box>

          {/* Test Summary */}
          {hasTestedRecords && (
            <>
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                >
                  Tests Completed
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  You&apos;ve tested &ldquo;{propertyDefinition.name}&rdquo; on{' '}
                  {results.length} record{results.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              <Alert
                severity="info"
                icon={<IconAlertCircle size={20} />}
                sx={{
                  '& .MuiAlert-message': {
                    fontSize: '13px',
                    lineHeight: '20px',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                >
                  Review Your Results
                </Typography>
                <Typography variant="body2">
                  Check if the property values match your expectations. If not,
                  refine the definition and test again.
                </Typography>
              </Alert>
            </>
          )}

          {/* Sample Results */}
          {results.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
              >
                Sample Results
              </Typography>
              <Stack spacing={1}>
                {results.slice(0, 3).map((result, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: (theme) => theme.palette.grey[50],
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                      >
                        {result.objectId}
                      </Typography>
                    </Box>
                    {result.value !== undefined && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Value: {JSON.stringify(result.value)}
                      </Typography>
                    )}
                    {result.reasoning && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {result.reasoning}
                      </Typography>
                    )}
                    {result.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                      >
                        Error: {result.error}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Actions */}
          <Stack
            spacing={1}
            sx={{ mt: 2 }}
          >
            {hasTestedRecords && (
              <>
                <Button
                  variant="contained"
                  startIcon={<IconChartColumn size={16} />}
                  onClick={onRunBackfill}
                  disabled={isProcessing}
                  fullWidth
                >
                  Proceed to Backfill
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<IconRefresh size={16} />}
                  onClick={onRefineDefinition}
                  disabled={isProcessing}
                  fullWidth
                >
                  Refine Definition
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      {hasTestedRecords && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          Tip: You can run more tests or proceed to backfill when satisfied with
          the results
        </Typography>
      )}
    </Stack>
  )
}

export default TestResultsReview
